/**
 * Why a money date can move by a day, said once and properly.
 *
 * The platform used to hold a generated bank-holiday calendar per market, and
 * with it an annual obligation to re-check the thing against the CNBV. It was
 * removed on 2026-09-24: every date it decided was the end of a WAIT, and a
 * wait that runs a day long harms nobody, while a day MISSING from a
 * hand-maintained file is a wait that ends early — money leaving before its
 * window closed. Counting Monday to Friday and nothing else errs in the safe
 * direction by construction, and has no data to go stale.
 *
 * What that costs is precision the client was never really owed: a date we show
 * can be a day before the bank actually opens. That is worth saying plainly,
 * once, in the same place every time — rather than hedging every sentence with
 * a parenthesis, which is how a message stops being read.
 *
 * NO ARRIVAL DAY IS EVER PREDICTED (owner's decision, 2026-09-25). None of this
 * copy says when the money will land — not "the next business day", not a date.
 * We do not control settlement: the rail does, through its own daily cut-off,
 * and the platform counts Monday to Friday without a holiday calendar. A day we
 * name is a promise we cannot keep, and the client who does not see the money
 * that day is right to call. What they are told instead is that the request is
 * SCHEDULED, which is the part that is true and the part that answers their
 * actual question — is anything still needed from me.
 *
 * And it says WHY it takes a while: the fund's country and the destination
 * account's country. That clause is not padding. Without it the message is
 * accurate and curt, and a limit with no reason reads as a brush-off — the
 * owner rejected exactly that wording on 2026-09-25. The reason is also true:
 * the vehicle banks in the US and destinations are MX / US / CA, so a
 * cross-border transfer genuinely takes longer than a domestic one. Giving a
 * MECHANISM is welcome; giving a control's rationale (anti-fraud, holds,
 * thresholds) is not, and that line is what keeps this from leaking.
 *
 * "A few days" stays inside the no-day rule: it names neither a day nor a
 * number, which is what the sweep test in the sibling spec enforces.
 *
 * Three tones, because the client is in one of three situations and the
 * reassurance they need is different in each:
 *
 *   standing  — looking at the form. What the rule is, before they commit.
 *   scheduled — they have asked. Nothing is waiting on them, and no day is named.
 *   dated     — they have been given a date by a REFUSAL. That is a different
 *               thing from an arrival day: a cooling-off window or a phone-change
 *               hold is our own wait, ends on our own clock, and the date is the
 *               client's cue to come back and try again. Those stay.
 *
 * Wording is provisional and will be reviewed by counsel; the structure is not.
 */

export type LegendTone = 'standing' | 'scheduled' | 'dated';

export interface SettlementLegend {
  readonly title: string;
  readonly body: string;
}

const LEGENDS: Record<LegendTone, SettlementLegend> = {
  standing: {
    title: 'Money moves on business days',
    body:
      'Transfers are sent Monday to Friday. Weekends and bank holidays are not '
      + 'business days.',
  },
  scheduled: {
    title: 'Your withdrawal is scheduled',
    body:
      'Nothing else is needed from you. Transfers are sent on business days, and how '
      + 'long one takes depends on the country your money is held in and the country of '
      + 'the account it is going to, so it can take a few days to process. We will let '
      + 'you know once the money has gone.',
  },
  dated: {
    title: 'About that date',
    body:
      'It counts business days, Monday to Friday. A bank holiday can move it by a '
      + 'day. Nothing is waiting on you in the meantime, and your place does not '
      + 'change.',
  },
};

export function settlementLegend(tone: LegendTone): SettlementLegend {
  return LEGENDS[tone];
}

/**
 * The legend a refusal should carry, or null when it should carry none.
 *
 * A refusal that ends on a date needs the caveat about that date. One that does
 * not end on a date — a blocked country, an account on hold pending a call —
 * gets nothing: an explanation of business days under a refusal that has
 * nothing to do with them is noise in the worst possible place.
 */
export function legendForRefusal(clearsOn: string | null): SettlementLegend | null {
  return clearsOn === null ? null : LEGENDS.dated;
}
